import React, { useState } from "react";
import { Modal, Form, Button, Row, Col, Card } from "react-bootstrap";
import { FaCalendarAlt, FaFileAlt, FaRegCalendarCheck, FaFilter, FaUsers } from 'react-icons/fa';
import { BsCalendarRange } from 'react-icons/bs';
import swal from "sweetalert2";

// --- FUNCIÓN UTILITARIA PARA FORMATEAR FECHAS ---
function formatearFechaPeru(fecha) {
    if (!fecha) return "-";

    // Caso Firestore Timestamp
    if (typeof fecha === "object" && typeof fecha.toDate === "function") {
        return fecha
            .toDate()
            .toLocaleString("es-PE", { timeZone: "America/Lima" });
    }

    // Caso string tipo "22-09-2024,22:23:037"
    if (typeof fecha === "string") {
        // Intentar parsear formato "dd-MM-yyyy,HH:mm:ss"
        const match = fecha.match(/^(\d{2})-(\d{2})-(\d{4}),(\d{2}):(\d{2}):(\d{2,})/);
        if (match) {
            const [_, d, m, y, h, min, s] = match;
            const dateObj = new Date(`${y}-${m}-${d}T${h}:${min}:${s.slice(0,2)}`);
            if (!isNaN(dateObj.getTime())) {
                return dateObj.toLocaleString("es-PE", { timeZone: "America/Lima" });
            }
        }
        // Si no matchea, mostrar tal cual
        return fecha;
    }

    // Si es Date
    if (fecha instanceof Date) {
        return fecha.toLocaleString("es-PE", { timeZone: "America/Lima" });
    }

    return "-";
}

function ModalReporteBoletas({ mostrar, onClose, onConfirm, usuariosSeleccionados = [] }) {
    const [configuracion, setConfiguracion] = useState({
        periodoInicio: "Enero",
        periodoFin: "Diciembre",
        tipoDocumento: "Boletas de pago",
        ano: new Date().getFullYear().toString(),
        incluirTodosLosUsuarios: false // Nueva opción
    });

    const monthToNumber = {
        "Enero": "01", "Febrero": "02", "Marzo": "03", "Abril": "04",
        "Mayo": "05", "Junio": "06", "Julio": "07", "Agosto": "08",
        "Setiembre": "09", "Octubre": "10", "Noviembre": "11", "Diciembre": "12"
    };

    const periodos = Object.keys(monthToNumber);
    const tiposDocumento = [
        { value: "Todos", label: "Todos los documentos" },
        { value: "Boletas de pago", label: "Boletas de Pago" },
        { value: "CTS", label: "CTS" },
        { value: "Contratos", label: "Contratos" },
        { value: "Renta", label: "Renta de 5ta" },
        { value: "Memorandums", label: "Memorándums" },
        { value: "Utilidades", label: "Utilidades" },
        { value: "Gratificaciones", label: "Gratificaciones" },
        { value: "Vacaciones", label: "Vacaciones" },
        { value: "Prestamos", label: "Préstamos" },
        { value: "Adelantos", label: "Adelantos" },
        { value: "PoliticasLaborales", label: "Política Salarial" },
        { value: "Ceses", label: "CESES Y LIQUIDACIONES" },
        { value: "Otros", label: "Otros" },
    ];
    const anos = ["2021", "2022", "2023", "2024", "2025", "2026", "2027", "2028", "2029", "2030"];

    const handlePeriodoInicioChange = (event) => {
        const { value } = event.target;
        setConfiguracion({ ...configuracion, periodoInicio: value });
    };

    const handlePeriodoFinChange = (event) => {
        const { value } = event.target;
        setConfiguracion({ ...configuracion, periodoFin: value });
    };

    const handleAnoChange = (event) => {
        const { value } = event.target;
        setConfiguracion({ ...configuracion, ano: value });
    };

    const handleTipoDocumentoChange = (event) => {
        const { value } = event.target;
        setConfiguracion({ ...configuracion, tipoDocumento: value });
    };

    const handleIncluirTodosChange = (event) => {
        const { checked } = event.target;
        setConfiguracion({ ...configuracion, incluirTodosLosUsuarios: checked });
    };

    const handleConfirm = () => {
        // Convierte los nombres de meses y tipos de documento seleccionados a números y texto
        const periodoInicioNumber = monthToNumber[configuracion.periodoInicio];
        const periodoFinNumber = monthToNumber[configuracion.periodoFin];
        const tipoDocumentoText = configuracion.tipoDocumento;
    
        // Valida que el período inicial sea menor que el período final
        if (periodoInicioNumber <= periodoFinNumber) {
            // Llama a la función onConfirm pasando configuración como argumento
            onConfirm({
                ...configuracion,
                periodoInicio: periodoInicioNumber,
                periodoFin: periodoFinNumber,
                tipoDocumento: tipoDocumentoText,
            });
        } else {
            swal.fire({
                title: "Error",
                text: "El período inicial debe ser menor o igual al período final.",
                icon: "error",
                confirmButtonText: "Aceptar",
            });
        }
    }

    return (
        <Modal 
            show={mostrar} 
            onHide={onClose} 
            backdrop="static" 
            size="lg"
            centered
            className="report-modal"
        >
            <Modal.Header closeButton className="bg-primary bg-gradient text-white py-3">
                <Modal.Title>
                    <div className="d-flex align-items-center gap-2">
                        <FaFileAlt />
                        <span>Configuración de Reporte PDF</span>
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4 bg-light">
                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-4">
                        <p className="text-muted mb-4">
                            Configure los parámetros para el reporte PDF que va a generar. Seleccione el período, año y tipo de documento deseado.
                        </p>

                        {/* Información de usuarios seleccionados */}
                        <div className="alert alert-info d-flex align-items-center gap-2 mb-4">
                            <FaUsers className="text-primary" />
                            <span>
                                {configuracion.incluirTodosLosUsuarios 
                                    ? "Se incluirán TODAS las boletas registradas en el sistema"
                                    : `Se incluirán las boletas de ${usuariosSeleccionados.length} usuario(s) seleccionado(s)`
                                }
                            </span>
                        </div>
                        
                        <Form>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Card className="h-100 bg-white shadow-sm border-0">
                                        <Card.Body className="p-3">
                                            <h5 className="text-primary mb-3 d-flex align-items-center gap-2">
                                                <BsCalendarRange className="me-1" />
                                                Período de Reporte
                                            </h5>
                                            <Row>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="d-flex align-items-center gap-1">
                                                            <FaCalendarAlt className="text-primary" size={14} />
                                                            <span>Inicio</span>
                                                        </Form.Label>
                                                        <Form.Select
                                                            name="periodoInicio"
                                                            value={configuracion.periodoInicio}
                                                            onChange={handlePeriodoInicioChange}
                                                            className="form-select border-primary shadow-sm"
                                                        >
                                                            {periodos.map((periodo) => (
                                                                <option key={periodo} value={periodo}>
                                                                    {periodo}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="d-flex align-items-center gap-1">
                                                            <FaCalendarAlt className="text-primary" size={14} />
                                                            <span>Fin</span>
                                                        </Form.Label>
                                                        <Form.Select
                                                            name="periodoFin"
                                                            value={configuracion.periodoFin}
                                                            onChange={handlePeriodoFinChange}
                                                            className="form-select border-primary shadow-sm"
                                                        >
                                                            {periodos.map((periodo) => (
                                                                <option key={periodo} value={periodo}>
                                                                    {periodo}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={6}>
                                    <Card className="h-100 bg-white shadow-sm border-0">
                                        <Card.Body className="p-3">
                                            <h5 className="text-primary mb-3 d-flex align-items-center gap-2">
                                                <FaFilter className="me-1" />
                                                Filtros
                                            </h5>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="d-flex align-items-center gap-1">
                                                    <FaRegCalendarCheck className="text-primary" size={14} />
                                                    <span>Año</span>
                                                </Form.Label>
                                                <Form.Select
                                                    name="ano"
                                                    value={configuracion.ano}
                                                    onChange={handleAnoChange}
                                                    className="form-select border-primary shadow-sm"
                                                >
                                                    {anos.map((ano) => (
                                                        <option key={ano} value={ano}>
                                                            {ano}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                            
                                            <Form.Group className="mb-0">
                                                <Form.Label className="d-flex align-items-center gap-1">
                                                    <FaFileAlt className="text-primary" size={14} />
                                                    <span>Tipo de Documento</span>
                                                </Form.Label>
                                                <Form.Select
                                                    name="tipoDocumento"
                                                    value={configuracion.tipoDocumento}
                                                    onChange={handleTipoDocumentoChange}
                                                    className="form-select border-primary shadow-sm"
                                                >
                                                    {tiposDocumento.map((tipo) => (
                                                        <option key={tipo.value} value={tipo.value}>
                                                            {tipo.label}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Nueva sección para opciones de usuarios */}
                            <Card className="bg-white shadow-sm border-0 mb-3">
                                <Card.Body className="p-3">
                                    <h5 className="text-primary mb-3 d-flex align-items-center gap-2">
                                        <FaUsers className="me-1" />
                                        Alcance del Reporte
                                    </h5>
                                    <div className="bg-light p-3 rounded">
                                        <Form.Check
                                            type="checkbox"
                                            id="incluirTodosUsuarios"
                                            label={
                                                <span className="fw-medium">
                                                    Incluir TODOS los documentos del sistema (no solo usuarios seleccionados)
                                                </span>
                                            }
                                            checked={configuracion.incluirTodosLosUsuarios}
                                            onChange={handleIncluirTodosChange}
                                            className="mb-2"
                                        />
                                        <small className="text-muted d-block">
                                            {configuracion.incluirTodosLosUsuarios 
                                                ? "⚠️ Se generará un reporte con TODOS los documentos que coincidan con los filtros seleccionados, independientemente de los usuarios seleccionados en la tabla."
                                                : `ℹ️ Se generará un reporte solo con los documentos de los ${usuariosSeleccionados.length} usuario(s) que tienes seleccionado(s) en la tabla.`
                                            }
                                        </small>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Form>
                    </Card.Body>
                </Card>
            </Modal.Body>
            <Modal.Footer className="bg-light border-top-0 p-3">
                <Button 
                    variant="outline-secondary" 
                    onClick={onClose}
                    className="px-4 py-2 fw-medium"
                >
                    Cancelar
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleConfirm}
                    className="px-4 py-2 fw-medium d-flex align-items-center gap-2"
                >
                    <FaFileAlt />
                    Generar PDF
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default ModalReporteBoletas;